import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import {
  FETCH_DATA_TAG_ALL_FILES,
  POP_UP_LOCATION_KEY,
} from "../DataTagConstants";
import "./DataTagAllFilesTable.scss";
import { renderDataTagNames } from "./DataTagUsersTable";
import FilterableHeader from "../Data Tag FIlters/FilterableHeader";
import DataTagFileSearch from "../Data Tag FIlters/DataTagFileSearch";
import { categoryIcons } from "../../../components/common/utilities";
import DataTagUserSearch from "../Data Tag FIlters/DataTagUserSearch";
import { Drawer, makeStyles } from "material-ui-core";
import RenderDataMapFilePopUp from "../../../components/shared/FilePopUp";
import { Tooltip } from "@material-ui/core";
import QuickFilters from "./QuickFilters";
import { sizeToBytes } from "../DataTagUtilities";
import ZoomOutMapIcon from "@material-ui/icons/ZoomOutMap";
import AspectRatioIcon from "@material-ui/icons/AspectRatio";

const SENSITIVITY_LOOKUP = {
  HIGH: { label: "High", className: "tag-high" },
  MEDIUM: { label: "Medium", className: "tag-medium" },
  LOW: { label: "Low", className: "tag-low" },
  NO_SENSITIVITY: { label: "None", className: "tag-none" },
};

const CATEGORY_LOOKUP = {
  ORGANIZATIONAL: { label: "Organizational", className: "cat-organizational" },
  PERSONAL: { label: "Personal", className: "cat-personal" },
  API: { label: "API", className: "cat-api" },
  FINANCIAL: { label: "Financial", className: "cat-financial" },
  MARKETING: { label: "Marketing", className: "cat-marketing" },
  MEDICAL: { label: "Medical", className: "cat-medical" },
  OTHER: { label: "Other", className: "cat-other" },
};

const useStyles = makeStyles({
  list: {
    width: 500,
    margin: "20px 20px",
  },
  fullList: {
    width: "auto",
  },
  paper: {
    background: "white",
  },
  paperReport: {
    background: "white",
    overflowY: "hidden",
  },
  "& .MuiFormControl": {
    width: "100%",
  },
});

const safe = (value) =>
  value === null || value === undefined || value === "" ? "-" : value;

export default function DataTagAllFilesTable() {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [fileNameExpanded, setFileNameExpanded] = useState(false);

  const dataSourceId = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.id,
  );

  const sourceName = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.type,
  );

  const tableData = useSelector(
    (state) => state.dataTagReducer.allFiles.content || [],
  );

  const totalCount = useSelector(
    (state) => state.dataTagReducer.allFiles?.totalCount || 0,
  );

  const summaryDataRaw = useSelector(
    (state) => state.dataTagReducer.categoryCardData,
  );

  const dataMapFilePopUp = useSelector(
    (state) => state.dataMapReducer.dataMapFilePopUp,
  );

  const sensitiveCardData = useSelector(
    (state) => state.dataTagReducer.sensitiveCardData,
  );

  const classificationStats = sensitiveCardData?.fileClassificationStatus || [];

  const fileTypesList = useSelector(
    (state) => state.dataTagReducer.fileTypesList,
  );

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });

  const [fileNameFilter, setFileNameFilter] = useState([]);
  const [usernameFilter, setUsernameFilter] = useState([]);
  const [selectedSensitivity, setSelectedSensitivity] = useState([]);
  const [categoryFilter, setSelectedCategoryFilter] = useState([]);
  const [regulatedFilter, setSelectedRegulatedFilter] = useState([]);
  const [typeFilter, setFileTypeFilter] = useState([]);
  const [extensionFilter, setExtensionFilter] = useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    lastAccessTime: false, // OFF initially
  });

  const filters = useMemo(() => {
    const set = new Set();
    classificationStats.forEach((item) => {
      item["FileClassificationStats.classificationCombination"]
        .split(",")
        .forEach((c) => set.add(c.trim()));
    });
    return Array.from(set);
  }, [classificationStats]);

  const toggleFilter = (type) => {
    setSelectedRegulatedFilter((prev = []) =>
      prev.includes(type)
        ? prev.filter((item) => item !== type)
        : [...prev, type],
    );
  };

  const handleFilePopUp = (data) => {
    if (data.row?.sensitivity !== "NO_SENSITIVITY") {
      dispatch({ type: "OPEN_FILE_POP_UP", payload: data.row });
      dispatch({ type: POP_UP_LOCATION_KEY, payload: "dataTag" });
    }
  };

  useEffect(() => {
    if (!dataSourceId) return;

    dispatch({
      type: FETCH_DATA_TAG_ALL_FILES,
      payload: {
        dataSourceId,
        page: paginationModel.page,
        size: paginationModel.pageSize,
        fileNames: fileNameFilter.length > 0 ? fileNameFilter : null,
        usernames: usernameFilter.length > 0 ? usernameFilter : null,
        sensitivity:
          selectedSensitivity.length > 0 ? selectedSensitivity : null,
        category: categoryFilter.length > 0 ? categoryFilter : null,
        regulations: regulatedFilter.length > 0 ? regulatedFilter : null,
        types: typeFilter.length > 0 ? typeFilter : null,
        extensions: extensionFilter.length > 0 ? extensionFilter : null,
      },
    });
  }, [
    dataSourceId,
    paginationModel.page,
    paginationModel.pageSize,
    fileNameFilter,
    selectedSensitivity,
    usernameFilter,
    categoryFilter,
    regulatedFilter,
    typeFilter,
    extensionFilter,
    dispatch,
  ]);

  const formatTime = (value) =>
    value
      ? `${dayjs(value).format("MM-DD-YYYY")} ${dayjs(value).format("HH:mm")}`
      : "-";

  const handlePaginationChange = (params) => {
    const newPagination = {
      pageSize: params.pageSize,
      page: params.page,
    };
    if (paginationModel.pageSize !== newPagination.pageSize) {
      newPagination.page = 0;
    }
    setPaginationModel(newPagination);
  };

  const columns = [
    {
      field: "fileName",
      headerName: "File Name",
      hideable: false,
      width: fileNameExpanded ? 600 : 350,
      minWidth: 250,
      maxWidth: 600,
      renderHeader: () => (
        <div className="filenameHeaderContainer">
          <FilterableHeader
            title="File Name"
            isFilterApplied={fileNameFilter.length > 0}
            onClear={() => setFileNameFilter([])}
          >
            <div>
              <DataTagFileSearch
                value={fileNameFilter}
                onChange={setFileNameFilter}
              />
            </div>
          </FilterableHeader>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setFileNameExpanded((prev) => !prev);
            }}
            className="fileNameExpandIconButton"
            title={fileNameExpanded ? "Shrink column" : "Expand column"}
          >
            {fileNameExpanded ? (
              <AspectRatioIcon style={{ fontSize: 16, color: "#8f8f8f" }} />
            ) : (
              <ZoomOutMapIcon style={{ fontSize: 16, color: "#8f8f8f" }} />
            )}
          </button>
        </div>
      ),
      renderCell: (p) => renderDataTagNames(p.value, fileNameExpanded),
    },
    {
      field: "owner",
      headerName: "Owner",
      hideable: false,
      flex: 2,
      renderHeader: () => (
        <FilterableHeader
          title="Owner"
          isFilterApplied={usernameFilter.length > 0}
          onClear={() => setUsernameFilter([])}
        >
          <DataTagUserSearch
            selectedValues={usernameFilter}
            onSelectionChange={(selected) => setUsernameFilter(selected)}
          />
        </FilterableHeader>
      ),
      renderCell: (p) => renderDataTagNames(p.value),
    },
    {
      field: "sensitivity",
      headerName: "Sensitivity",
      hideable: false,
      flex: 1,
      renderCell: (p) => {
        if (!p.value) return "-";
        const info =
          SENSITIVITY_LOOKUP[p.value] || SENSITIVITY_LOOKUP.NO_SENSITIVITY;
        return (
          <span className={`tag-chip ${info.className}`}>{info.label}</span>
        );
      },
    },
    {
      field: "regulations",
      headerName: "Regulations",
      hideable: false,
      flex: 1.7,
      renderCell: (params) => {
        const values = Array.isArray(params.value) ? params.value : [];
        if (values.length === 0) return "-";

        const visible = fileNameExpanded
          ? values.slice(0, 1)
          : values.slice(0, 2);
        const remaining = fileNameExpanded ? values.slice(1) : values.slice(2);

        return (
          <div className="regulation-cell">
            {visible.map((item) => (
              <span key={item} className="regulation-pill">
                {item}
              </span>
            ))}
            {remaining.length > 0 && (
              <Tooltip
                title={
                  <div className="regulation-tooltip-content">
                    {remaining.map((item) => (
                      <div key={item} className="tooltip-row">
                        {item}
                      </div>
                    ))}
                  </div>
                }
                placement="top"
                arrow
              >
                <span className="regulation-more">+{remaining.length}</span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      field: "category",
      headerName: "Category",
      hideable: false,
      flex: 1.2,
      renderCell: (params) => {
        if (!params.value) return "-";

        const values = params.value
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean);

        if (values.length === 0) return "-";

        const visible = fileNameExpanded
          ? values.slice(0, 1)
          : values.slice(0, 2);

        const remaining = fileNameExpanded ? values.slice(1) : values.slice(2);

        return (
          <div className="category-icon-cell">
            {visible.map((item) => {
              const cat = CATEGORY_LOOKUP[item];

              return categoryIcons[item] ? (
                <Tooltip key={item} title={cat?.label || item}>
                  <img
                    className="categoryIconsDataTag"
                    src={categoryIcons[item]}
                    alt={item}
                  />
                </Tooltip>
              ) : null;
            })}

            {remaining.length > 0 && (
              <Tooltip
                title={
                  <div className="regulation-tooltip-content">
                    {remaining.map((item) => (
                      <div key={item} className="tooltip-row category">
                        {categoryIcons[item] && (
                          <img
                            src={categoryIcons[item]}
                            className="categoryIconsDataTag"
                            alt={item}
                          />
                        )}
                        {CATEGORY_LOOKUP[item]?.label || item}
                      </div>
                    ))}
                  </div>
                }
                placement="top"
                arrow
                classes={{
                  tooltip: "custom-tooltip",
                  arrow: "custom-tooltip-arrow",
                }}
              >
                <span className="regulation-more">+{remaining.length}</span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      field: "fileType",
      headerName: "File Type",
      hideable: false,
      flex: 0.7,
      headerAlign: "center",
      align: "center",
      renderCell: (p) => safe(p.value),
    },
    {
      field: "fieldSize",
      headerName: "File Size",
      hideable: false,
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (p) => safe(p.value),
      sortComparator: (v1, v2) => {
        return sizeToBytes(v1) - sizeToBytes(v2);
      },
    },

    {
      field: "lastScanTime",
      headerName: "Last Scan",
      hideable: false,
      flex: 1.2,
      renderCell: (p) => formatTime(p.value),
    },
    {
      field: "lastAccessTime",
      headerName: "Last Accessed",
      flex: 1,
      renderCell: (p) => formatTime(p.value),
    },
  ];

  return (
    <div className="dt-all-files-table">
      <QuickFilters
        showCategory={summaryDataRaw.length > 0}
        categoryValue={categoryFilter}
        onCategoryChange={setSelectedCategoryFilter}
        sensitivityValue={selectedSensitivity}
        onSensitivityChange={setSelectedSensitivity}
        regulatedValue={regulatedFilter}
        onRegulatedChange={toggleFilter}
        filters={filters}
        typeFilterList={fileTypesList}
        typeFilter={typeFilter}
        onTypeFilterValue={setFileTypeFilter}
        extensionFilter={extensionFilter}
        onExtensionFilter={setExtensionFilter}
      />

      <DataGrid
        rows={tableData.map((r, index) => ({ id: index + 1, ...r }))}
        columns={columns}
        className="mui-table"
        rowCount={totalCount}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        onRowClick={handleFilePopUp}
        disableColumnFilter
        disableColumnSelector={false}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        getTogglableColumns={(columns) =>
          columns
            .filter((col) => col.field === "lastAccessTime")
            .map((col) => col.field)
        }
        sx={{
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "hidden",
          },
        }}
      />

      <Drawer
        classes={{ paper: classes.paper }}
        anchor="right"
        open={dataMapFilePopUp}
        onClose={() => dispatch({ type: "CLOSE_FILE_POP_UP" })}
      >
        <RenderDataMapFilePopUp sourceName={sourceName} />
      </Drawer>
    </div>
  );
}
